<app-page-loader *ngIf="loading"></app-page-loader>

<ng-container *ngIf="!loading">
  <p *ngIf="!view" class="lm-note">{{ I18N + ".unavailable" | translate }}</p>

  <ng-container *ngIf="view as v">
    <div class="lm-head">
      <span class="al-status-chip" [ngClass]="v.memory.enabled ? 'al-status-chip--accept' : 'al-status-chip--warning'">
        {{ (v.memory.enabled ? I18N + ".memoryOn" : I18N + ".memoryOff") | translate }}
      </span>
      <span *ngIf="updated" class="lm-muted">{{ I18N + ".updated" | translate: { date: updated } }}</span>
    </div>

    <p *ngIf="v.empty && !course" class="lm-note">{{ I18N + ".empty" | translate }}</p>

    <!-- Key figures: ring · scale · streak · conversations -->
    <div class="lm-kpis" *ngIf="!v.empty">
      <div class="lm-tile lm-kpi" *ngIf="v.figures.exercises">
        <svg [attr.width]="ring.size" [attr.height]="ring.size" [attr.viewBox]="'0 0 ' + ring.size + ' ' + ring.size"
             role="img" [attr.aria-label]="I18N + '.kpi.exercisesAria' | translate: { correct: v.figures.exercises_correct, total: v.figures.exercises }">
          <circle [attr.cx]="ring.size / 2" [attr.cy]="ring.size / 2" [attr.r]="ring.r" class="lm-ring__track"></circle>
          <circle [attr.cx]="ring.size / 2" [attr.cy]="ring.size / 2" [attr.r]="ring.r" class="lm-ring__fill"
                  [attr.stroke-dasharray]="exerciseDash.array" [attr.stroke-dashoffset]="exerciseDash.offset"
                  [attr.transform]="'rotate(-90 ' + ring.size / 2 + ' ' + ring.size / 2 + ')'"></circle>
          <text x="50%" y="53%" text-anchor="middle" dominant-baseline="middle" class="lm-ring__text">
            {{ exercisePct * 100 | number: "1.0-0" }}%
          </text>
        </svg>
        <div>
          <div class="lm-kpi__label">{{ I18N + ".fig.exercises" | translate }}</div>
          <div class="lm-kpi__value">{{ v.figures.exercises_correct }}<small> / {{ v.figures.exercises }}</small></div>
        </div>
      </div>

      <div class="lm-tile" *ngIf="knowledge >= 0">
        <div class="lm-kpi__label">{{ I18N + ".fact.knowledge_level" | translate }}</div>
        <div class="lm-kpi__value lm-kpi__value--text">{{ knowledgeLabel }}</div>
        <div class="lm-scale" role="img" [attr.aria-label]="knowledgeLabel">
          <span *ngFor="let s of scaleSteps; let i = index" [class.on]="i <= knowledge"></span>
        </div>
        <div class="lm-scale__ends"><span>{{ I18N + ".level.beginner" | translate }}</span><span>{{ I18N + ".level.professional" | translate }}</span></div>
      </div>

      <div class="lm-tile">
        <div class="lm-kpi__label">{{ I18N + ".kpi.streak" | translate }}</div>
        <div class="lm-kpi__value">{{ v.figures.streak_days }}<small> {{ I18N + ".kpi.days" | translate }}</small></div>
        <div class="lm-streak" aria-hidden="true"><span *ngFor="let on of streakDots" [class.on]="on"></span></div>
      </div>

      <div class="lm-tile">
        <div class="lm-kpi__label">{{ I18N + ".fig.interactions" | translate }}</div>
        <div class="lm-kpi__value">{{ v.figures.interactions }}</div>
        <svg *ngIf="spark" class="lm-spark" viewBox="0 0 140 30" preserveAspectRatio="none" aria-hidden="true">
          <polyline [attr.points]="'0,30 ' + spark + ' 140,30'" class="lm-spark__area"></polyline>
          <polyline [attr.points]="spark" class="lm-spark__line"></polyline>
        </svg>
        <div class="lm-kpi__hint" *ngIf="spark">{{ I18N + ".kpi.sparkHint" | translate }}</div>
      </div>
    </div>

    <!-- Strengths & gaps · exercise topics -->
    <div class="lm-two" *ngIf="course || topics.length">
      <section class="lm-tile" *ngIf="course">
        <header class="lm-tile__head">
          <h5>{{ I18N + ".strengthsTitle" | translate }}<small *ngIf="course.course_name"> · {{ course.course_name }}</small></h5>
          <app-segmented-control [options]="lensOptions" [value]="lens" [ariaLabel]="I18N + '.strengthsTitle' | translate"
                                 (valueChange)="setLens($event)"></app-segmented-control>
        </header>
        <app-competency-radar *ngIf="radarAxes.length && radarTexts" [axes]="radarAxes" [texts]="radarTexts"
                              [ariaLabel]="radarAria"></app-competency-radar>
        <ng-container *ngIf="!radarAxes.length">
          <ul class="lm-stack" *ngIf="mastery.length; else noAxes">
            <li *ngFor="let m of mastery; trackBy: trackKey" class="lm-stack__row">
              <span class="lm-stack__label" [title]="m.label">{{ m.label }}</span>
              <span class="lm-stack__bar" [style.width.%]="m.width" role="img" [attr.aria-label]="m.aria">
                <span *ngFor="let s of m.segments" [style.flex]="s.n" [style.background]="s.color"></span>
              </span>
              <span class="lm-stack__n">{{ m.count }}</span>
            </li>
          </ul>
          <ul class="lm-legend" *ngIf="mastery.length">
            <li *ngFor="let tier of TIERS"><i [style.background]="TIER_COLOR[tier]"></i>{{ I18N + ".tier." + tier | translate }}</li>
            <li class="lm-legend__end">{{ I18N + ".masteryCount" | translate }}</li>
          </ul>
          <ng-template #noAxes><p class="lm-note">{{ I18N + ".noAxes" | translate }}</p></ng-template>
        </ng-container>
      </section>

      <section class="lm-tile" *ngIf="topics.length">
        <header class="lm-tile__head"><h5>{{ I18N + ".exercisesTitle" | translate }}<small> · {{ I18N + ".sortedByResult" | translate }}</small></h5></header>
        <ul class="lm-topics">
          <li *ngFor="let tb of topics; trackBy: trackKey">
            <span class="lm-topics__name" [title]="tb.label">{{ tb.label }}</span>
            <span class="lm-dots" role="img" [attr.aria-label]="tb.tierLabel">
              <span *ngFor="let d of tb.dots" [style.background]="d ? tb.color : null"></span>
            </span>
            <span class="lm-track" role="img" [attr.aria-label]="tb.meta || tb.tierLabel"><span [style.width.%]="tb.pct" [style.background]="tb.color"></span></span>
            <span class="lm-topics__meta">
              {{ tb.tierLabel }}<ng-container *ngIf="tb.meta"> · {{ tb.meta }}</ng-container>
              <span *ngFor="let w of tb.weak" class="al-status-chip al-status-chip--warning lm-chip-sm">{{ I18N + ".unsure" | translate: { name: w } }}</span>
            </span>
          </li>
        </ul>
      </section>
    </div>

    <!-- Mastery trend -->
    <section class="lm-tile" *ngIf="trendOptions">
      <header class="lm-tile__head"><h5>{{ I18N + ".trendTitle" | translate }}<small> · {{ I18N + ".trendHint" | translate }}</small></h5></header>
      <div class="lm-trend" echarts [options]="trendOptions" theme="alphabees" role="img"
           [attr.aria-label]="I18N + '.trendTitle' | translate"></div>
      <ul class="lm-legend">
        <li *ngFor="let tier of TIERS"><i [style.background]="TIER_COLOR[tier]"></i>{{ I18N + ".tier." + tier | translate }}</li>
      </ul>
    </section>

    <!-- Learning behaviour -->
    <section class="lm-tile" *ngIf="behaviour.length || solo">
      <header class="lm-tile__head"><h5>{{ I18N + ".behaviourTitle" | translate }}<small> · {{ I18N + ".behaviourHint" | translate }}</small></h5></header>
      <div class="lm-behaviour" *ngIf="behaviour.length">
        <div *ngFor="let b of behaviour; trackBy: trackKey">
          <div class="lm-kpi__label">{{ b.label }}</div>
          <div class="lm-behaviour__num">{{ b.value }}<small> {{ b.unit }}</small></div>
          <div class="lm-raster" [attr.data-kind]="b.key" aria-hidden="true"><span *ngFor="let d of b.dots" [class]="d"></span></div>
          <p class="lm-note">{{ b.note }}</p>
        </div>
      </div>
      <!-- Answer depth (SOLO, PRD mastery-objectives M5) -->
      <div class="lm-solo" *ngIf="solo">
        <div class="lm-kpi__label">{{ I18N + ".solo.title" | translate }}</div>
        <div class="lm-behaviour__num">{{ solo.total }}<small> {{ I18N + ".solo.answers" | translate }}</small></div>
        <span class="lm-solo__bar" role="img" [attr.aria-label]="solo.aria">
          <span *ngFor="let s of solo.segments; trackBy: trackKey" [style.flex]="s.n" [style.background]="s.color" [title]="s.title"></span>
        </span>
        <ul class="lm-legend">
          <li *ngFor="let l of solo.legend; trackBy: trackKey"><i [style.background]="l.color"></i>{{ l.label }} · {{ l.n }}</li>
        </ul>
        <p class="lm-note">{{ I18N + ".solo.hint" | translate }}</p>
      </div>
    </section>

    <!-- What the agent noticed · activity -->
    <div class="lm-two" *ngIf="v.observations.total || hasActivity">
      <section class="lm-tile" *ngIf="v.observations.total">
        <header class="lm-tile__head"><h5>{{ I18N + ".observationsTitle" | translate }}<small> · {{ I18N + ".observationsHint" | translate }}</small></h5></header>
        <div class="lm-toolbar">
          <app-segmented-control [options]="typeOptions" [value]="typeFilter" [ariaLabel]="I18N + '.filterLabel' | translate"
                                 (valueChange)="setType($event)"></app-segmented-control>
          <app-search-input class="lm-search" [value]="query" [placeholderKey]="I18N + '.searchPlaceholder'"
                            (valueChange)="setQuery($event)"></app-search-input>
        </div>
        <p class="lm-note" *ngIf="!bars.length">{{ I18N + ".noMatch" | translate }}</p>
        <div class="lm-obs" *ngFor="let g of bars; trackBy: trackKey">
          <button type="button" class="lm-obs__row" [attr.aria-expanded]="open.has(g.key)" (click)="toggle(g.key)">
            <span class="lm-stack__label" [title]="g.label">{{ g.label }}</span>
            <span class="lm-stack__bar" [style.width.%]="g.width" role="img" [attr.aria-label]="g.label + ': ' + g.count">
              <span *ngFor="let s of g.segments" [style.flex]="s.n" [style.background]="s.color" [title]="s.title"></span>
            </span>
            <span class="lm-stack__n">{{ g.count }}</span>
          </button>
          <ul class="lm-obs__items" *ngIf="open.has(g.key)">
            <li *ngFor="let o of g.items">
              <i [style.background]="TYPE_COLOR[o.type]" aria-hidden="true"></i>
              <span>{{ o.text }}</span>
              <small>{{ o.typeLabel }}<ng-container *ngIf="o.meta"> · {{ o.meta }}</ng-container></small>
            </li>
          </ul>
        </div>
        <ul class="lm-legend">
          <li *ngFor="let type of TYPE_ORDER"><i [style.background]="TYPE_COLOR[type]"></i>{{ I18N + ".type." + type | translate }}</li>
        </ul>
      </section>

      <section class="lm-tile" *ngIf="hasActivity">
        <header class="lm-tile__head"><h5>{{ I18N + ".activityTitle" | translate }}</h5></header>
        <app-bar-chart class="lm-chart" [loading]="false" [labels]="chartLabels" [series]="chartSeries" [stacked]="true"></app-bar-chart>
      </section>
    </div>

    <!-- Profile chips · summary -->
    <section class="lm-tile" *ngIf="chips.length || v.summary.text">
      <header class="lm-tile__head"><h5>{{ I18N + ".profileTitle" | translate }}</h5></header>
      <div class="lm-facts" *ngIf="chips.length">
        <span class="lm-fact" *ngFor="let c of chips">
          <nb-icon [icon]="c.icon" aria-hidden="true"></nb-icon><span class="lm-muted">{{ c.label }}</span><b>{{ c.value }}</b>
        </span>
      </div>
      <details class="lm-summary" *ngIf="v.summary.text">
        <summary>
          <span class="lm-summary__head">
            <h5>{{ I18N + ".summaryTitle" | translate }}</h5>
            <app-segmented-control [options]="summaryOptions" [value]="summaryMode" [ariaLabel]="I18N + '.summaryTitle' | translate"
                                   (valueChange)="setSummaryMode($event)" (click)="$event.preventDefault()"></app-segmented-control>
          </span>
          <p class="lm-summary__text">{{ summaryText }}</p>
          <span class="lm-summary__more">{{ I18N + ".more" | translate }}</span>
        </summary>
      </details>
    </section>

    <section class="lm-section" *ngIf="moodleRows.length">
      <h5>{{ I18N + ".moodleTitle" | translate }}</h5>
      <app-table [title]="I18N + '.moodleTitle' | translate" [data]="moodleRows" [columns]="moodleColumns"
                 [selectable]="false" [maxVisibleItems]="10"></app-table>
    </section>
  </ng-container>
</ng-container>
